import { Link, useSearchParams } from "react-router";
import { BookOpen, ChevronRight, Folder, Home } from "lucide-react";
import {
  ASSET_TYPES,
  ASSET_TYPE_LABEL,
  type AssetType,
} from "@colab/shared";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  usePortalResources,
  usePortalResourceDetail,
  type ContentAssetRow,
} from "./use-portal-resources";
import { ResourceCard } from "./resource-card";

// Phase 7 sub-bucket 8 — port of /app/portal/resources/page.tsx.
// Single page with optional `?folder=<id>` deep-link. Folders surface above
// the asset grid; assets group by asset_type into a Tabs view ("All" tab is
// always present when at least one asset exists). The legacy used native RSC
// to fetch in parallel; here both reads fire in parallel via TanStack Query
// (the folder-detail read is gated by `enabled: !!folderId`).

export function ResourcesPage() {
  const [params] = useSearchParams();
  const folderId = params.get("folder");

  const assetsQuery = usePortalResources(folderId);
  const folderQuery = usePortalResourceDetail(folderId);

  const assets = assetsQuery.data ?? [];
  const folder = folderQuery.data ?? null;

  const folders = assets.filter((a) => a.is_folder);
  const items = assets.filter((a) => !a.is_folder);

  const grouped = new Map<AssetType, ContentAssetRow[]>();
  for (const t of ASSET_TYPES) grouped.set(t, []);
  for (const a of items) {
    grouped.get(a.asset_type)?.push(a);
  }

  return (
    <div>
      <header className="mb-7">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Resources
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
          {folder ? folder.title : "Resources"}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {folder
            ? (folder.description ??
              "Resources inside this folder, curated by the Bookboost team.")
            : "Enablement content from Bookboost — decks, embedded videos, slides, and meeting links."}
        </p>
      </header>

      {/* Breadcrumb */}
      <nav className="mb-4 flex items-center gap-1 text-sm">
        <Link
          to="/portal/resources"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Home className="h-3.5 w-3.5" />
          All resources
        </Link>
        {folder && (
          <>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="rounded-md px-1.5 py-0.5 font-medium">{folder.title}</span>
          </>
        )}
      </nav>

      {assetsQuery.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : (
        <>
          {folders.length > 0 && (
            <section className="mb-6">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Folders
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {folders.map((f) => (
                  <Link
                    key={f.id}
                    to={`/portal/resources?folder=${f.id}`}
                    className="group flex items-center gap-3 rounded-xl border bg-card p-4 transition hover:border-foreground/15 hover:bg-muted/30"
                  >
                    <Folder className="h-5 w-5 text-amber-500" />
                    <div className="min-w-0">
                      <div className="truncate font-display text-sm font-semibold tracking-tight">
                        {f.title}
                      </div>
                      {f.description && (
                        <div className="truncate text-xs text-muted-foreground">
                          {f.description}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {items.length > 0 ? (
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="mb-4 bg-transparent p-0">
                <TabsTrigger value="all">All ({items.length})</TabsTrigger>
                {ASSET_TYPES.filter((t) => (grouped.get(t)?.length ?? 0) > 0).map((t) => (
                  <TabsTrigger key={t} value={t}>
                    {ASSET_TYPE_LABEL[t]} ({grouped.get(t)?.length ?? 0})
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="all">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((a) => (
                    <ResourceCard key={a.id} asset={a} />
                  ))}
                </div>
              </TabsContent>
              {ASSET_TYPES.map((t) => {
                const list = grouped.get(t) ?? [];
                if (list.length === 0) return null;
                return (
                  <TabsContent key={t} value={t}>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {list.map((a) => (
                        <ResourceCard key={a.id} asset={a} />
                      ))}
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          ) : folders.length === 0 ? (
            <EmptyState
              title={folder ? "Nothing in this folder yet" : "No resources yet"}
              description={
                folder
                  ? "The Bookboost team hasn't published anything inside this folder yet."
                  : "The Bookboost team hasn't published any content for your tier yet. Check back soon."
              }
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-card px-6 py-14 text-center">
      <BookOpen className="h-6 w-6 text-muted-foreground" />
      <h3 className="font-display text-base font-semibold tracking-tight">{title}</h3>
      <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
